import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    List,
    ListItem,
    ListItemButton,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
    Avatar,
    Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { queryItemTypesWithLocations, queryPackingListItems, ItemType } from '../schema';
import { useState, useMemo } from 'react';
import { calculateAvailableQuantity } from '../utils/inventory';
import { db } from '../instant';
import { id } from '@instantdb/react';

interface Props {
    open: boolean;
    onClose: () => void;
    eventId: string;
    onItemAdded: () => void;
}

interface QuantityDialogProps {
    open: boolean;
    itemType: ItemType | null;
    onClose: () => void;
    onConfirm: (quantity: number) => void;
}

function QuantityDialog({ open, itemType, onClose, onConfirm }: QuantityDialogProps) {
    const [quantityInput, setQuantityInput] = useState('1');

    const quantity = (() => {
        const val = parseInt(quantityInput, 10);
        return Number.isInteger(val) && val >= 1 ? val : NaN;
    })();
    const isValid = !Number.isNaN(quantity);

    const handleConfirm = () => {
        if (isValid) {
            onConfirm(quantity);
            setQuantityInput('1');
        }
    };

    const handleClose = () => {
        setQuantityInput('1');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>Add to Packing List</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {itemType?.name}
                    </Typography>
                    <TextField
                        label="Quantity"
                        type="number"
                        value={quantityInput}
                        onChange={e => setQuantityInput(e.target.value)}
                        inputProps={{ min: 1 }}
                        fullWidth
                        autoFocus
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleConfirm} variant="contained" disabled={!isValid}>
                    Add to Packing List
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function AddPackingListItemDialog({ open, onClose, eventId, onItemAdded }: Props) {
    const { data: itemTypesData } = queryItemTypesWithLocations();
    const { data: packingListData } = queryPackingListItems(eventId);
    const [searchText, setSearchText] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string>('');
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [quantityDialogOpen, setQuantityDialogOpen] = useState(false);
    const [selectedItemType, setSelectedItemType] = useState<ItemType | null>(null);

    const event = packingListData?.events?.[0];
    const existingPackingListItemIds = useMemo(
        () => new Set((event?.packingListItems || []).map((item: any) => item.itemType?.id).filter(Boolean)),
        [event?.packingListItems]
    );

    // Get all itemTypes and categories
    const allItemTypes = useMemo(() => (itemTypesData?.itemTypes || []) as ItemType[], [itemTypesData?.itemTypes]);
    const allItemLocations = useMemo(() => {
        const locations: Array<{
            id: string;
            quantity: number;
            itemType?: { id: string } | null;
            event?: { id: string } | null;
        }> = [];

        for (const itemType of allItemTypes) {
            const itemLocations = (itemType as any).itemLocations || [];
            for (const location of itemLocations) {
                locations.push({
                    id: location.id,
                    quantity: location.quantity || 0,
                    itemType: { id: itemType.id },
                    event: location.event ? { id: location.event.id } : null,
                });
            }
        }
        return locations;
    }, [allItemTypes]);

    // Filter and group items
    const filteredItemsAndCategories = useMemo(() => {
        const categoriesSet = new Set<string>();
        const filtered: ItemType[] = [];

        for (const itemType of allItemTypes) {
            // Exclude items already in packing list
            if (existingPackingListItemIds.has(itemType.id)) {
                continue;
            }

            // Filter by search text
            if (searchText) {
                const searchLower = searchText.toLowerCase();
                const matchesName = itemType.name?.toLowerCase().includes(searchLower);
                const matchesDescription = itemType.description?.toLowerCase().includes(searchLower);
                if (!matchesName && !matchesDescription) {
                    continue;
                }
            }

            // Filter by category
            if (categoryFilter && itemType.category?.id !== categoryFilter) {
                continue;
            }

            filtered.push(itemType);
            if (itemType.category?.id) {
                categoriesSet.add(itemType.category.id);
            }
        }

        // Sort items by name
        filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

        // Group by category
        const itemsByCategory = new Map<string, ItemType[]>();
        for (const item of filtered) {
            const categoryId = item.category?.id || 'uncategorized';
            if (!itemsByCategory.has(categoryId)) {
                itemsByCategory.set(categoryId, []);
            }
            const bucket = itemsByCategory.get(categoryId);
            if (bucket === undefined) throw new Error('itemsByCategory: missing bucket');
            bucket.push(item);
        }

        // Get unique categories
        const categoryIds: string[] = [];
        for (const id of categoriesSet) categoryIds.push(id);
        const categoriesList = categoryIds
            .map(id => {
                const item = filtered.find(i => i.category?.id === id);
                return item?.category;
            })
            .filter(Boolean)
            .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));

        return {
            filteredItems: itemsByCategory,
            categories: categoriesList,
        };
    }, [allItemTypes, existingPackingListItemIds, searchText, categoryFilter]);

    const { filteredItems, categories } = filteredItemsAndCategories;

    const handleAddClick = (itemType: ItemType) => {
        setSelectedItemType(itemType);
        setQuantityDialogOpen(true);
    };

    const handleQuantityConfirm = async (quantity: number) => {
        if (!selectedItemType) return;

        try {
            const packingListItemId = id();
            await db.transact([
                db.tx.packingListItems[packingListItemId].update({
                    quantity,
                    confirmed: false,
                }),
                db.tx.packingListItems[packingListItemId].link({ itemType: selectedItemType.id }),
                db.tx.packingListItems[packingListItemId].link({ event: eventId }),
            ]);

            setQuantityDialogOpen(false);
            setSelectedItemType(null);
            onItemAdded();
        } catch (error) {
            console.error('Error adding packing list item:', error);
            // TODO: Show error toast
        }
    };

    const toggleCategory = (categoryId: string) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    };

    const handleClose = () => {
        setSearchText('');
        setCategoryFilter('');
        onClose();
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Add Item to Packing List</Typography>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {/* Search Bar */}
                        <TextField
                            placeholder="Search by name or description..."
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            fullWidth
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Category Filter */}
                        <FormControl size="small" fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select value={categoryFilter} label="Category" onChange={e => setCategoryFilter(e.target.value)}>
                                <MenuItem value="">All Categories</MenuItem>
                                {categories.map(category => (
                                    <MenuItem key={category?.id} value={category?.id || ''}>
                                        {category?.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Item List */}
                        <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
                            <List dense>
                                {Array.from(filteredItems.entries()).map(([categoryId, items]) => {
                                    const category = categories.find(c => c?.id === categoryId);
                                    const isExpanded = expandedCategories.has(categoryId);
                                    const categoryColor = category?.color || '#2196F3';

                                    return (
                                        <Box key={categoryId}>
                                            {/* Category Header */}
                                            <ListItemButton
                                                onClick={() => toggleCategory(categoryId)}
                                                sx={{
                                                    bgcolor: 'action.hover',
                                                    borderBottom: '1px solid',
                                                    borderColor: 'divider',
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    <Chip
                                                        label={category?.name || 'Uncategorized'}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: categoryColor,
                                                            color: 'white',
                                                            fontSize: '0.7rem',
                                                        }}
                                                    />
                                                    <Typography variant="body2" color="text.secondary">
                                                        ({items.length})
                                                    </Typography>
                                                </Box>
                                            </ListItemButton>

                                            {/* Category Items */}
                                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                <List component="div" disablePadding>
                                                    {items.map(item => {
                                                        const availableQty = calculateAvailableQuantity(item.id, allItemLocations, eventId);

                                                        return (
                                                            <ListItem
                                                                key={item.id}
                                                                secondaryAction={
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <Typography
                                                                            variant="body2"
                                                                            color="text.secondary"
                                                                            sx={{
                                                                                fontSize: '0.75rem',
                                                                                minWidth: '80px',
                                                                                textAlign: 'right',
                                                                                '& span': {
                                                                                    fontFamily: 'monospace',
                                                                                },
                                                                            }}
                                                                        >
                                                                            Available: <span>{availableQty}</span>
                                                                        </Typography>
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            startIcon={<AddIcon />}
                                                                            onClick={() => handleAddClick(item)}
                                                                        >
                                                                            Add
                                                                        </Button>
                                                                    </Box>
                                                                }
                                                            >
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, pr: 2 }}>
                                                                    <Avatar
                                                                        src={item.photoUrl || undefined}
                                                                        variant="rounded"
                                                                        sx={{
                                                                            width: 40,
                                                                            height: 40,
                                                                            bgcolor: 'grey.200',
                                                                        }}
                                                                    >
                                                                        {!item.photoUrl && <LocalOfferIcon />}
                                                                    </Avatar>
                                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                                                            {item.name}
                                                                        </Typography>
                                                                        {item.description && (
                                                                            <Typography
                                                                                variant="body2"
                                                                                color="text.secondary"
                                                                                sx={{
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    whiteSpace: 'nowrap',
                                                                                }}
                                                                            >
                                                                                {item.description}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            </ListItem>
                                                        );
                                                    })}
                                                </List>
                                            </Collapse>
                                        </Box>
                                    );
                                })}
                            </List>

                            {filteredItems.size === 0 && (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {existingPackingListItemIds.size > 0
                                            ? 'All items already in packing list'
                                            : 'No items match search'}
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>

            <QuantityDialog
                open={quantityDialogOpen}
                itemType={selectedItemType}
                onClose={() => {
                    setQuantityDialogOpen(false);
                    setSelectedItemType(null);
                }}
                onConfirm={handleQuantityConfirm}
            />
        </>
    );
}
